import weakMemoize from "@-/com.npmjs.emotion__weak-memoize";
import { getGraphemesByTier } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.data.prc.tier";
import {
  type GraphemeTaiwanTbclSet,
  type GraphemeTaiwanUsageMap,
  taiwanUsageClassificationBand,
} from "@-/dev.improve.haniverse.data.taiwan-usage-map";
import type { DecompositionMap } from "#decomposition-map";
import arrayLength from "@-/com.npmjs.extra-array__length";

/**
 * Decomposition components bucketed by the most prevalent characters they build. The three sets
 * are pairwise disjoint: a component reachable from a `common` character is only in
 * `commonComponentSet`, even when it also builds `uncommon` / `rare` characters.
 */
export type GraphemeComponentPrevalenceLookup = {
  /** Components of `common` characters (PRC tier 1 / Taiwan TBCL). */
  readonly commonComponentSet: ReadonlySet<string>;
  /** Components of `rare` characters (PRC tier 3 / Taiwan 次常用字). */
  readonly rareComponentSet: ReadonlySet<string>;
  /** Components of `uncommon` characters (PRC tier 2 / Taiwan 常用字 without a TBCL level). */
  readonly uncommonComponentSet: ReadonlySet<string>;
};

/**
 * BFS-extends `componentSet` (and `seen`) with every component reachable from `seedGraphemes`.
 * Uses a head-index queue instead of `Array.prototype.shift()` (O(n) per shift on V8/Hermes).
 * Components already present in any of `excluders` are not written to `componentSet` — this is
 * how each lower-prevalence pass stays disjoint from the passes above it without a second sweep.
 */
function extendReachableComponents(
  seedGraphemes: Iterable<string>,
  decompositionMap: DecompositionMap,
  seen: Set<string>,
  componentSet: Set<string>,
  excluders: readonly ReadonlySet<string>[],
): void {
  const queue: string[] = [];
  for (const seed of seedGraphemes) {
    if (!seen.has(seed)) {
      queue.push(seed);
    }
  }
  for (let head = 0; head < arrayLength(queue); head++) {
    const grapheme = queue[head];
    if (grapheme === undefined) {
      continue;
    }
    if (seen.has(grapheme)) {
      continue;
    }
    seen.add(grapheme);
    const components = decompositionMap.get(grapheme);
    if (components === undefined) {
      continue;
    }
    for (const component of components) {
      if (!excluders.some((excluder) => excluder.has(component))) {
        componentSet.add(component);
      }
      if (!seen.has(component)) {
        queue.push(component);
      }
    }
  }
}

/**
 * Build the three disjoint closures in prevalence order: every component reachable from a
 * `common` seed lands in the common set, then the uncommon pass only adds components not already
 * claimed, and so on. `obscure` characters contribute nothing — being a building block of an
 * obscure character is no evidence of prevalence.
 */
function buildGraphemeComponentPrevalenceLookup(
  commonGraphemes: Iterable<string>,
  uncommonGraphemes: Iterable<string>,
  rareGraphemes: Iterable<string>,
  decompositionMap: DecompositionMap,
): GraphemeComponentPrevalenceLookup {
  const seen = new Set<string>();
  const commonComponentSet = new Set<string>();
  extendReachableComponents(commonGraphemes, decompositionMap, seen, commonComponentSet, []);
  const uncommonComponentSet = new Set<string>();
  extendReachableComponents(uncommonGraphemes, decompositionMap, seen, uncommonComponentSet, [
    commonComponentSet,
  ]);
  const rareComponentSet = new Set<string>();
  extendReachableComponents(rareGraphemes, decompositionMap, seen, rareComponentSet, [
    commonComponentSet,
    uncommonComponentSet,
  ]);
  return {
    commonComponentSet,
    rareComponentSet,
    uncommonComponentSet,
  };
}

/**
 * Memoize on object identity. Inputs come from react-query with `staleTime: Infinity`, so for the
 * lifetime of a session every consumer hands us the same frozen object and the WeakMap entries
 * survive — replicating the previous `onetime` behaviour without pinning a global cache to a
 * single value.
 */

/** PRC: tier-1 components → `common`; tier-2 → `uncommon`; tier-3 → `rare`. */
export const getSimplifiedComponentPrevalenceLookup = weakMemoize(
  (decompositionMap: DecompositionMap): GraphemeComponentPrevalenceLookup => {
    const { tier1, tier2, tier3 } = getGraphemesByTier();
    return buildGraphemeComponentPrevalenceLookup(tier1, tier2, tier3, decompositionMap);
  },
);

const traditionalLookupByDecompositionMap = weakMemoize((_decompositionMap: DecompositionMap) =>
  weakMemoize((usageMap: GraphemeTaiwanUsageMap) =>
    weakMemoize((tbclSet: GraphemeTaiwanTbclSet): GraphemeComponentPrevalenceLookup => {
      const commonSeeds: string[] = [];
      const uncommonSeeds: string[] = [];
      const rareSeeds: string[] = [];
      // A TBCL glyph is common even when it is absent from the usage map (a glyph can carry a
      // level without being chart-listed), so seed from the union of both sources.
      const glyphs = new Set<string>([...Object.keys(usageMap), ...tbclSet]);
      for (const glyph of glyphs) {
        const band = taiwanUsageClassificationBand(usageMap[glyph], tbclSet.has(glyph));
        switch (band) {
          case "common": {
            commonSeeds.push(glyph);
            break;
          }
          case "uncommon": {
            uncommonSeeds.push(glyph);
            break;
          }
          case "rare": {
            rareSeeds.push(glyph);
            break;
          }
        }
      }
      return buildGraphemeComponentPrevalenceLookup(
        commonSeeds,
        uncommonSeeds,
        rareSeeds,
        _decompositionMap,
      );
    }),
  ),
);

/**
 * Taiwan: TBCL-glyph components → `common`; other 常用字 components → `uncommon`; 次常用字
 * components → `rare` (mirrors `taiwanUsageClassificationBand`).
 */
export function getTraditionalComponentPrevalenceLookup(
  decompositionMap: DecompositionMap,
  usageMap: GraphemeTaiwanUsageMap,
  tbclSet: GraphemeTaiwanTbclSet,
): GraphemeComponentPrevalenceLookup {
  return traditionalLookupByDecompositionMap(decompositionMap)(usageMap)(tbclSet);
}
